/**
 * Static copy for the marketing landing page.
 *
 * Titles and body text are verbatim from the previous Index.tsx — the design
 * handoff calls for the existing wording, not a rewrite. Kept here so the
 * section components stay presentational and the copy has one home.
 */

export type JourneyStep = {
  n: number;
  title: string;
  body: string;
  /** Tailwind classes for the number badge. */
  badge: string;
};

/**
 * The six-step entrepreneur journey.
 *
 * The badge color sequence walks the three EAB brand pairs light-then-dark and
 * is deliberate per the handoff — do not reorder. Step 2 is the one exception to
 * white badge text: #8FCA3F is too light to carry white legibly.
 */
export const journeySteps: JourneyStep[] = [
  {
    n: 1,
    title: "Create Your Profile",
    body: "Create your free EAB Connect profile and become part of a global community of entrepreneurs, mentors, investors, and partners working together to build sustainable businesses.",
    badge: "bg-eab-orange text-white",
  },
  {
    n: 2,
    title: "Activate Your Entrepreneur Journey",
    body: "Once your membership is approved, you'll begin a guided learning experience designed to help you assess where you are, clarify what you're building, and identify what support will move you forward fastest.",
    badge: "bg-eab-green text-eab-ink",
  },
  {
    n: 3,
    title: "Build Your Founder Story",
    body: "Through hands-on coursework, you'll develop a customized pitch deck and founder profile that clearly communicates your business, your needs, and your goals—so the right people can find and support you.",
    badge: "bg-eab-blue text-white",
  },
  {
    n: 4,
    title: "Become an EAB Certified Entrepreneur",
    body: "Complete the core learning modules to earn EAB certification, signaling your readiness to engage deeper with the community and unlock additional resources and opportunities.",
    badge: "bg-eab-orange-dark text-white",
  },
  {
    n: 5,
    title: "Connect to Mentors, Capital, and Opportunity",
    body: "As a certified EAB community member, you gain access to curated mentors, live events, advanced learning, partnership opportunities, and pathways to funding—matched to your stage and ambitions.",
    badge: "bg-eab-green-dark text-white",
  },
  {
    n: 6,
    title: "Give Back and Grow Forward",
    body: "As you progress, you'll have opportunities to support other EAB community members—sharing insights, offering feedback, mentoring, and collaborating—strengthening the ecosystem while deepening your own leadership and entrepreneurial skills.",
    badge: "bg-eab-blue-dark text-white",
  },
];

export const entrepreneurPoints = [
  "Access to experienced business mentors",
  "Algorithm-matched suggestions based on your needs (Coming Soon!)",
  "15-minute focused sessions",
  "Up to 3 mentor requests per week",
];

export const mentorPoints = [
  "Give back to the entrepreneurial community",
  "Discover investment opportunities",
  "Expand your professional network",
  "Flexible time commitment",
];

/** Sign-up routes, matching what Index.tsx wired previously. */
export const routes = {
  signUp: "/auth?tab=signup",
  signUpEntrepreneur: "/auth?tab=signup&role=entrepreneur",
  signUpMentor: "/auth?tab=signup&role=mentor",
  signIn: "/auth",
  news: "/news",
} as const;

/**
 * Shared side padding and max width for landing sections. The design is drawn
 * at 1440px with 56px side padding, collapsing to 20px on small screens.
 */
export const sectionShell = "mx-auto w-full max-w-[1440px] px-5 lg:px-14";
